import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '../../../components/ui';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../../components/ui/Select';
import { 
  Tooltip, 
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ZAxis
} from 'recharts';
import { 
  MapPin, 
  Clock, 
  Smartphone, 
  Monitor, 
  Mail,
  TrendingUp,
  Users,
  Activity,
  Zap
} from 'lucide-react';

// Simple theme hook replacement
const useTheme = () => ({ theme: 'dark' });

// Simple tabs components
const Tabs: React.FC<{ children: React.ReactNode; defaultValue?: string; value?: string; onValueChange?: (value: string) => void; className?: string }> = ({ children, className }) => (
  <div className={className}>{children}</div>
);

const TabsList: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground ${className}`}>
    {children}
  </div>
);

const TabsTrigger: React.FC<{ children: React.ReactNode; value: string; className?: string }> = ({ children, className }) => (
  <button className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm ${className}`}>
    {children}
  </button>
);

const TabsContent: React.FC<{ children: React.ReactNode; value: string; className?: string }> = ({ children, className }) => (
  <div className={`mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}>
    {children}
  </div>
);

interface EngagementHeatmapProps {
  campaignId: string;
}

interface HeatmapData {
  geographic: Array<{
    country: string;
    region: string;
    lat: number;
    lng: number;
    opens: number;
    clicks: number;
    replies: number;
    intensity: number;
    engagementRate: number;
  }>;
  temporal: Array<{
    hour: number;
    day: string;
    opens: number;
    clicks: number;
    replies: number;
    intensity: number;
  }>;
  device: Array<{
    deviceType: string;
    timeRange: string;
    engagement: number;
    intensity: number;
  }>;
  content: Array<{
    linkIndex: number;
    linkText: string;
    url: string;
    clicks: number;
    position: { x: number; y: number };
    heatScore: number;
  }>;
}

export function EngagementHeatmap({ campaignId }: EngagementHeatmapProps) {
  const { theme } = useTheme();
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState<'geographic' | 'temporal' | 'device' | 'content'>('geographic');
  const [timeGranularity, setTimeGranularity] = useState<'hour' | 'day' | 'week'>('hour');

  const colors = useMemo(() => ({
    primary: theme === 'dark' ? '#3b82f6' : '#2563eb',
    secondary: theme === 'dark' ? '#8b5cf6' : '#7c3aed',
    success: theme === 'dark' ? '#10b981' : '#059669',
    warning: theme === 'dark' ? '#f59e0b' : '#d97706',
    danger: theme === 'dark' ? '#ef4444' : '#dc2626',
    muted: theme === 'dark' ? '#6b7280' : '#9ca3af',
    background: theme === 'dark' ? '#1f2937' : '#f8fafc',
    surface: theme === 'dark' ? '#374151' : '#ffffff',
    text: theme === 'dark' ? '#f9fafb' : '#111827'
  }), [theme]);

  useEffect(() => {
    const loadHeatmapData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/email-analytics/heatmap/${campaignId}?granularity=${timeGranularity}`);
        
        if (!response.ok) throw new Error('Failed to load heatmap data');
        
        const data = await response.json();
        setHeatmapData(data);
      } catch (error) {
        console.error('Failed to load heatmap data:', error);
        // Mock data for demo
        setHeatmapData({
          geographic: [
            { country: 'United States', region: 'California', lat: 37.7749, lng: -122.4194, opens: 150, clicks: 45, replies: 8, intensity: 0.8, engagementRate: 0.35 },
            { country: 'United States', region: 'New York', lat: 40.7128, lng: -74.0060, opens: 120, clicks: 38, replies: 6, intensity: 0.7, engagementRate: 0.32 },
            { country: 'Canada', region: 'Toronto', lat: 43.6532, lng: -79.3832, opens: 75, clicks: 22, replies: 4, intensity: 0.5, engagementRate: 0.29 },
            { country: 'United Kingdom', region: 'London', lat: 51.5074, lng: -0.1278, opens: 85, clicks: 26, replies: 5, intensity: 0.6, engagementRate: 0.31 }
          ],
          temporal: generateTemporalData(),
          device: [
            { deviceType: 'mobile', timeRange: '9AM-12PM', engagement: 85, intensity: 0.8 },
            { deviceType: 'desktop', timeRange: '2PM-5PM', engagement: 120, intensity: 0.9 },
            { deviceType: 'tablet', timeRange: '7PM-10PM', engagement: 45, intensity: 0.4 }
          ],
          content: [
            { linkIndex: 0, linkText: 'Learn More', url: 'https://example.com/learn', clicks: 89, position: { x: 30, y: 45 }, heatScore: 0.9 },
            { linkIndex: 1, linkText: 'Get Started', url: 'https://example.com/start', clicks: 156, position: { x: 50, y: 60 }, heatScore: 1.0 },
            { linkIndex: 2, linkText: 'Contact Us', url: 'https://example.com/contact', clicks: 34, position: { x: 75, y: 80 }, heatScore: 0.3 }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    loadHeatmapData();
  }, [campaignId, timeGranularity]);

  const generateTemporalData = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const data = [];
    
    for (let day of days) {
      for (let hour = 0; hour < 24; hour++) {
        const baseEngagement = Math.random() * 50;
        const peakHours = [9, 10, 11, 14, 15, 16, 19, 20];
        const isPeak = peakHours.includes(hour);
        const multiplier = isPeak ? 2.5 : 1;
        
        data.push({
          hour,
          day,
          opens: Math.round(baseEngagement * multiplier * 0.6),
          clicks: Math.round(baseEngagement * multiplier * 0.2),
          replies: Math.round(baseEngagement * multiplier * 0.05),
          intensity: Math.min(1, baseEngagement * multiplier / 100)
        });
      }
    }
    
    return data;
  };

  const getIntensityColor = (intensity: number) => {
    if (intensity > 0.8) return colors.danger;
    if (intensity > 0.6) return colors.warning;
    if (intensity > 0.4) return colors.primary;
    if (intensity > 0.2) return colors.secondary;
    return colors.muted;
  };

  const renderGeographicHeatmap = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MapPin className="h-5 w-5" />
            <span>Global Engagement Map</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* World Map Visualization (would use actual map library) */}
            <div className="relative bg-muted/20 rounded-lg p-6 h-64 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Interactive world map would be here</p>
                <p className="text-xs text-muted-foreground mt-2">Showing engagement intensity by location</p>
              </div>
              
              {/* Overlay engagement hotspots */}
              <div className="absolute inset-0 pointer-events-none">
                {heatmapData?.geographic.map((location, index) => (
                  <div
                    key={index}
                    className="absolute w-4 h-4 rounded-full animate-pulse"
                    style={{
                      backgroundColor: getIntensityColor(location.intensity),
                      left: `${(location.lng + 180) / 360 * 100}%`,
                      top: `${(90 - location.lat) / 180 * 100}%`,
                      opacity: location.intensity
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Top Performing Locations */}
            <div className="space-y-4">
              <h4 className="font-semibold">Top Performing Locations</h4>
              {heatmapData?.geographic
                .sort((a, b) => b.engagementRate - a.engagementRate)
                .slice(0, 5)
                .map((location, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg">
                  <div>
                    <div className="font-medium">{location.region}, {location.country}</div>
                    <div className="text-sm text-muted-foreground">
                      {location.opens} opens, {location.clicks} clicks
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" style={{ backgroundColor: getIntensityColor(location.intensity) }}>
                      {(location.engagementRate * 100).toFixed(1)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTemporalHeatmap = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Time-based Engagement Patterns</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Time Granularity Selector */}
            <div className="flex justify-between items-center">
              <Select value={timeGranularity} onValueChange={(value: any) => setTimeGranularity(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hour">Hourly</SelectItem>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.danger }} />
                  <span>High</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.warning }} />
                  <span>Medium</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.primary }} />
                  <span>Low</span>
                </div>
              </div>
            </div>

            {/* Heatmap Grid */}
            <div className="grid grid-cols-8 gap-1">
              {/* Days header */}
              <div></div>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="text-xs text-center text-muted-foreground p-2">
                  {day}
                </div>
              ))}
              
              {/* Hour rows */}
              {Array.from({ length: 24 }, (_, hour) => (
                <React.Fragment key={hour}>
                  <div className="text-xs text-muted-foreground p-2 text-right">
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                    const dataPoint = heatmapData?.temporal.find(d => d.hour === hour && d.day === day);
                    const intensity = dataPoint?.intensity || 0;
                    
                    return (
                      <div
                        key={`${day}-${hour}`}
                        className="aspect-square rounded cursor-pointer transition-all hover:scale-110"
                        style={{
                          backgroundColor: getIntensityColor(intensity),
                          opacity: Math.max(0.1, intensity)
                        }}
                        title={`${day} ${hour}:00 - ${dataPoint?.opens || 0} opens, ${dataPoint?.clicks || 0} clicks`}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>

            {/* Peak Times Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-primary">9-11 AM</div>
                  <div className="text-sm text-muted-foreground">Peak Open Times</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-secondary">2-4 PM</div>
                  <div className="text-sm text-muted-foreground">Peak Click Times</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-success">Tue-Thu</div>
                  <div className="text-sm text-muted-foreground">Best Days</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderDeviceHeatmap = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Smartphone className="h-5 w-5" />
            <span>Device & Time Cross-Analysis</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart data={heatmapData?.device || []}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.muted} />
              <XAxis 
                type="category" 
                dataKey="timeRange" 
                stroke={colors.text}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                type="category" 
                dataKey="deviceType" 
                stroke={colors.text}
                tick={{ fontSize: 12 }}
              />
              <ZAxis type="number" dataKey="engagement" range={[50, 400]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.muted}`,
                  borderRadius: '8px'
                }}
                formatter={(value, name) => [value, name]}
              />
              <Scatter dataKey="engagement" fill={colors.primary}>
                {heatmapData?.device.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getIntensityColor(entry.intensity)} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );

  const renderContentHeatmap = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Content Engagement Hotspots</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Email Layout Heatmap */}
            <div className="relative bg-muted/10 rounded-lg p-6 h-96 border-2 border-dashed border-muted">
              <div className="absolute top-4 left-4 right-4 text-center">
                <div className="text-sm text-muted-foreground">Email Header</div>
              </div>
              
              {/* Content hotspots */}
              {heatmapData?.content.map((hotspot, index) => (
                <div
                  key={index}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                  style={{
                    left: `${hotspot.position.x}%`,
                    top: `${hotspot.position.y}%`
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse"
                    style={{ backgroundColor: getIntensityColor(hotspot.heatScore) }}
                    title={`${hotspot.linkText}: ${hotspot.clicks} clicks`}
                  >
                    {hotspot.clicks}
                  </div>
                </div>
              ))}
              
              <div className="absolute bottom-4 left-4 right-4 text-center">
                <div className="text-sm text-muted-foreground">Email Footer</div>
              </div>
            </div>

            {/* Content Performance List */}
            <div className="space-y-3">
              <h4 className="font-semibold">Link Performance</h4>
              {heatmapData?.content
                .sort((a, b) => b.clicks - a.clicks)
                .map((link, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg">
                  <div>
                    <div className="font-medium">{link.linkText}</div>
                    <div className="text-sm text-muted-foreground truncate max-w-64">
                      {link.url}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{link.clicks}</div>
                    <Badge variant="outline" style={{ backgroundColor: getIntensityColor(link.heatScore) }}>
                      {(link.heatScore * 100).toFixed(0)}% heat
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Zap className="h-4 w-4 animate-spin" />
          <span>Loading engagement heatmap...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Engagement Heatmap</h2>
          <p className="text-muted-foreground">
            Visual analysis of engagement patterns across time, location, and content
          </p>
        </div>
        
        <Select value={selectedView} onValueChange={(value: any) => setSelectedView(value)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="geographic">Geographic</SelectItem>
            <SelectItem value="temporal">Time-based</SelectItem>
            <SelectItem value="device">Device</SelectItem>
            <SelectItem value="content">Content</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selectedView === 'geographic' && renderGeographicHeatmap()}
      {selectedView === 'temporal' && renderTemporalHeatmap()}
      {selectedView === 'device' && renderDeviceHeatmap()}
      {selectedView === 'content' && renderContentHeatmap()}
    </div>
  );
}

export default EngagementHeatmap; 